import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert, 
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  BugReport as BugReportIcon,
  CheckBox as CheckBoxIcon,
  Grade as GradeIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { usersApi, classificationApi, severityApi, ratingApi, organizationSettingsApi } from '../../services/api';
import { useAuth } from '../../hooks/auth/useAuth';
import EditSLADialog from './EditSLADialog';
import DefectTypeDialog from './DefectTypeDialog';
import SeveritySchemeDialog from './SeveritySchemeDialog';
import SeverityLevelDialog from './SeverityLevelDialog';
import InspectionTypeDialog from './InspectionTypeDialog';
import RatingSchemeDialog from './RatingSchemeDialog';
import RatingLevelDialog from './RatingLevelDialog';
import InspectionCycleRules from './InspectionCycleRules';

// Add inspection types API service
const inspectionTypesApi = {
  getTypes: (params) => {
    const api = require('../../services/api').default;
    return api.get('/inspections/types', { params });
  },
  createType: (data) => {
    const api = require('../../services/api').default;
    return api.post('/inspections/types', data);
  },
  updateType: (id, data) => {
    const api = require('../../services/api').default;
    return api.put(`/inspections/types/${id}`, data);
  },
  deleteType: (id) => {
    const api = require('../../services/api').default;
    return api.delete(`/inspections/types/${id}`);
  }
};

const SettingsPage = () => {
  // Authentication context
  const { user, isLoading: authLoading } = useAuth();
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Defect types and SLA data
  const [defectTypes, setDefectTypes] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [defectTypesLoading, setDefectTypesLoading] = useState(false);
  const [defectTypesError, setDefectTypesError] = useState(null);
  
  // Inspection types data
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [inspectionTypesLoading, setInspectionTypesLoading] = useState(false);
  const [inspectionTypesError, setInspectionTypesError] = useState(null);
  
  // Asset Type Management data
  const [severitySchemes, setSeveritySchemes] = useState([]);
  const [severityLevels, setSeverityLevels] = useState({});
  const [assetManagementLoading, setAssetManagementLoading] = useState(false);
  const [assetManagementError, setAssetManagementError] = useState(null);
  
  // Rating Management data
  const [ratingSchemes, setRatingSchemes] = useState({});
  const [ratingLevels, setRatingLevels] = useState({});
  const [ratingManagementLoading, setRatingManagementLoading] = useState(false);
  const [ratingManagementError, setRatingManagementError] = useState(null);
  
  // Accordion expansion state
  const [expandedPanels, setExpandedPanels] = useState({});

  // CSV Export state
  const [isExportingAssets, setIsExportingAssets] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Auto-assign settings state
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [autoAssignLoading, setAutoAssignLoading] = useState(false);
  const [autoAssignError, setAutoAssignError] = useState(null);
  const [autoAssignSaving, setAutoAssignSaving] = useState(false);

  // Edit SLA Dialog state
  const [editSLADialog, setEditSLADialog] = useState({
    open: false,
    defectType: null
  });

  // Asset Type Management Dialog states
  const [defectTypeDialog, setDefectTypeDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    assetTypeId: null,
    defectType: null
  });

  const [severitySchemeDialog, setSeveritySchemeDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    assetTypeId: null,
    scheme: null
  });

  const [severityLevelDialog, setSeverityLevelDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    schemeId: null,
    level: null
  });

  const [ratingSchemeDialog, setRatingSchemeDialog] = useState({
    open: false,
    mode: 'create',
    assetTypeId: null,
    scheme: null
  });

  const [ratingLevelDialog, setRatingLevelDialog] = useState({
    open: false,
    mode: 'create',
    schemeId: null,
    level: null
  });

  // Inspection Type Dialog state
  const [inspectionTypeDialog, setInspectionTypeDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    assetTypeId: null,
    inspectionType: null
  });
  
  useEffect(() => {
    const fetchData = async () => {
      if (authLoading || !user) {
        // Don't fetch data if authentication is still loading or user is not available
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Use the authenticated user data directly
        setUserData(user);
        
        // Fetch defect types and asset types for this user's organization
        if (user.organizationId) {
          await Promise.all([
            fetchDefectTypes(user.organizationId),
            fetchAssetTypes(),
            // Only fetch asset management data for Managers
            user.role === 'manager' ? fetchAssetManagementData(user.organizationId) : Promise.resolve(),
            // Only fetch auto-assign setting for Managers
            user.role === 'manager' ? fetchAutoAssignSetting() : Promise.resolve(),
            // user.role === 'manager' ? fetchRatingData(user.organizationId) : Promise.resolve() // This will be handled on accordion expand
          ]);
        } else {
          setError('No organization associated with your account');
        }
      } catch (err) {
        setError('Failed to fetch settings data');
        console.error('Error fetching settings data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading, user]);

  // Fetch auto-assign setting
  const fetchAutoAssignSetting = async () => {
    try {
      setAutoAssignLoading(true);
      setAutoAssignError(null);
      const response = await organizationSettingsApi.get('defect_auto_assign');
      setAutoAssignEnabled(response.data?.value?.enabled || false);
    } catch (err) {
      console.error('Error fetching auto-assign setting:', err);
      setAutoAssignError('Failed to load auto-assign setting');
    } finally {
      setAutoAssignLoading(false);
    }
  };

  // Handle auto-assign toggle
  const handleAutoAssignToggle = async (event) => {
    const newValue = event.target.checked;
    try {
      setAutoAssignSaving(true);
      setAutoAssignError(null);
      await organizationSettingsApi.update('defect_auto_assign', { enabled: newValue });
      setAutoAssignEnabled(newValue);
    } catch (err) {
      console.error('Error updating auto-assign setting:', err);
      setAutoAssignError('Failed to update auto-assign setting');
      // Revert the toggle on error
      setAutoAssignEnabled(!newValue);
    } finally {
      setAutoAssignSaving(false);
    }
  };

  const fetchDefectTypes = async (organizationId) => {
    try {
      setDefectTypesLoading(true);
      setDefectTypesError(null);

      const response = await classificationApi.getDefectTypes({
        organization_id: organizationId
      });

      setDefectTypes(response.data || []);
    } catch (err) {
      setDefectTypesError('Failed to fetch defect types');
      console.error('Error fetching defect types:', err);
    } finally {
      setDefectTypesLoading(false);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const response = await classificationApi.getTypes();
      setAssetTypes(response.data || []);
    } catch (err) {
      console.error('Error fetching asset types:', err);
    }
  };

  const fetchInspectionTypes = async (organizationId) => {
    try {
      setInspectionTypesLoading(true);
      setInspectionTypesError(null);
      
      const response = await inspectionTypesApi.getTypes({
        organization_id: organizationId
      });
      
      setInspectionTypes(response.data || []);
    } catch (err) {
      setInspectionTypesError('Failed to fetch inspection types');
      console.error('Error fetching inspection types:', err);
    } finally {
      setInspectionTypesLoading(false);
    }
  };

  const fetchAssetManagementData = async (organizationId) => {
    try {
      setAssetManagementLoading(true);
      setAssetManagementError(null);
      
      // Fetch severity schemes for this organization using the API service
      const schemesResponse = await severityApi.getSchemes({
        organization_id: organizationId
      });
      
      const schemes = schemesResponse.data || [];
      setSeveritySchemes(schemes);
      
      // Fetch severity levels for each scheme
      const levelsPromises = schemes.map(async (scheme) => {
        try {
          const levelsResponse = await severityApi.getLevels({
            scheme_id: scheme.id
          });
          return { schemeId: scheme.id, levels: levelsResponse.data || [] };
        } catch (err) {
          console.error(`Error fetching levels for scheme ${scheme.id}:`, err);
          return { schemeId: scheme.id, levels: [] };
        }
      });
      
      const levelsResults = await Promise.all(levelsPromises);
      const levelsMap = {};
      levelsResults.forEach(({ schemeId, levels }) => {
        levelsMap[schemeId] = levels;
      });
      setSeverityLevels(levelsMap);

      // Also fetch inspection types for managers
      await fetchInspectionTypes(organizationId);
      
    } catch (err) {
      setAssetManagementError('Failed to fetch asset management data');
      console.error('Error fetching asset management data:', err);
    } finally {
      setAssetManagementLoading(false);
    }
  };

  const fetchRatingData = async (organizationId, assetTypeId) => {
    try {
      setRatingManagementLoading(true);
      setRatingManagementError(null);

      console.log('Fetching rating data for organization:', organizationId, 'assetType:', assetTypeId);
      
      const schemesResponse = await ratingApi.getSchemes({
        organization_id: organizationId,
        asset_type_id: assetTypeId,
      });
      
      // Handle both wrapped and unwrapped response formats
      const responsePayload = schemesResponse.data;
      const schemes = Array.isArray(responsePayload)
        ? responsePayload
        : (Array.isArray(responsePayload?.data) ? responsePayload.data : []);

      console.log('Rating schemes response:', schemesResponse);
      
      console.log('Processed schemes:', schemes, 'Is array:', Array.isArray(schemes));
      
      setRatingSchemes(prev => {
        const newState = {
          ...prev,
          [assetTypeId]: schemes
        };
        console.log('Setting rating schemes state:', newState);
        return newState;
      });
      
      const levelsPromises = schemes.map(async (scheme) => {
        try {
          const levelsResponse = await ratingApi.getLevels({
            scheme_id: scheme.id
          });

          const levelsPayload = levelsResponse.data;
          const levelsArray = Array.isArray(levelsPayload)
            ? levelsPayload
            : (Array.isArray(levelsPayload?.data) ? levelsPayload.data : []);
          return { schemeId: scheme.id, levels: levelsArray };
        } catch (err) {
          console.error(`Error fetching levels for rating scheme ${scheme.id}:`, err);
          return { schemeId: scheme.id, levels: [] };
        }
      });
      
      const levelsResults = await Promise.all(levelsPromises);
      const newLevelsMap = {};
      levelsResults.forEach(({ schemeId, levels }) => {
        newLevelsMap[schemeId] = levels;
      });
      // Merge new levels with existing levels map
      setRatingLevels(prev => ({...prev, ...newLevelsMap}));
      
    } catch (err) {
      setRatingManagementError('Failed to fetch rating management data');
      console.error('Error fetching rating management data:', err);
    } finally {
      setRatingManagementLoading(false);
    }
  };

  const formatSLAHours = (hours) => {
    if (!hours) return '-';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const getSLAStatus = (tempHours, permHours) => {
    if (tempHours && permHours) return 'Both';
    if (tempHours) return 'Temp Only';
    if (permHours) return 'Perm Only';
    return 'None';
  };

  const getSLAStatusColor = (status) => {
    switch (status) {
      case 'Both': return 'success';
      case 'Temp Only': return 'warning';
      case 'Perm Only': return 'info';
      default: return 'default';
    }
  };

  // Group defect types by asset type
  const groupDefectTypesByAsset = () => {
    const grouped = {};
    const generalDefects = [];

    defectTypes.forEach(defectType => {
      if (defectType.asset_type_id) {
        if (!grouped[defectType.asset_type_id]) {
          grouped[defectType.asset_type_id] = [];
        }
        grouped[defectType.asset_type_id].push(defectType);
      } else {
        generalDefects.push(defectType);
      }
    });

    return { grouped, generalDefects };
  };

  // Group severity schemes by asset type
  const groupSeveritySchemesByAsset = () => {
    const grouped = {};
    
    severitySchemes.forEach(scheme => {
      if (scheme.asset_type_id) {
        if (!grouped[scheme.asset_type_id]) {
          grouped[scheme.asset_type_id] = [];
        }
        grouped[scheme.asset_type_id].push(scheme);
      }
    });

    return grouped;
  };

  const getAssetTypeName = (assetTypeId) => {
    const assetType = assetTypes.find(type => type.id === assetTypeId);
    return assetType?.name || `Asset Type ${assetTypeId}`;
  };

  const handleAccordionChange = (panel, assetTypeId) => (event, isExpanded) => {
    console.log('Accordion change - panel:', panel, 'assetTypeId:', assetTypeId, 'type:', typeof assetTypeId, 'isExpanded:', isExpanded);
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
    if (isExpanded && user?.organizationId && assetTypeId) {
      fetchRatingData(user.organizationId, assetTypeId);
    }
  };

  const handleEditSLA = (defectType) => {
    setEditSLADialog({
      open: true,
      defectType: defectType
    });
  };

  const handleCloseSLADialog = () => {
    setEditSLADialog({
      open: false,
      defectType: null
    });
  };

  const handleSLAUpdate = async () => {
    // Refresh defect types data after successful update
    if (user?.organizationId) {
      await fetchDefectTypes(user.organizationId);
    }
  };

  // Asset Type Management handlers
  const handleCreateDefectType = (assetTypeId) => {
    setDefectTypeDialog({
      open: true,
      mode: 'create',
      assetTypeId,
      defectType: null
    });
  };

  const handleEditDefectType = (defectType) => {
    setDefectTypeDialog({
      open: true,
      mode: 'edit',
      assetTypeId: defectType.asset_type_id,
      defectType
    });
  };

  const handleCreateSeverityScheme = (assetTypeId) => {
    setSeveritySchemeDialog({
      open: true,
      mode: 'create',
      assetTypeId,
      scheme: null
    });
  };

  const handleEditSeverityScheme = (scheme) => {
    setSeveritySchemeDialog({
      open: true,
      mode: 'edit',
      assetTypeId: scheme.asset_type_id,
      scheme
    });
  };

  const handleCreateSeverityLevel = (schemeId) => {
    setSeverityLevelDialog({
      open: true,
      mode: 'create',
      schemeId,
      level: null
    });
  };

  const handleEditSeverityLevel = (level, schemeId) => {
    setSeverityLevelDialog({
      open: true,
      mode: 'edit',
      schemeId,
      level
    });
  };

  // Rating Management handlers
  const handleCreateRatingScheme = (assetTypeId) => {
    setRatingSchemeDialog({
      open: true,
      mode: 'create',
      assetTypeId,
      scheme: null
    });
  };

  const handleEditRatingScheme = (scheme) => {
    setRatingSchemeDialog({
      open: true,
      mode: 'edit',
      assetTypeId: scheme.asset_type_id,
      scheme
    });
  };

  const handleCreateRatingLevel = (schemeId) => {
    setRatingLevelDialog({
      open: true,
      mode: 'create',
      schemeId,
      level: null
    });
  };

  const handleEditRatingLevel = (level, schemeId) => {
    setRatingLevelDialog({
      open: true,
      mode: 'edit',
      schemeId,
      level
    });
  };

  const refreshAssetManagementData = async () => {
    if (user?.organizationId) {
      await Promise.all([
        fetchDefectTypes(user.organizationId),
        fetchAssetManagementData(user.organizationId),
        // fetchRatingData is now called on accordion expand or after an update
      ]);
    }
  };

  const refreshRatingsForAssetType = async (assetTypeId) => {
    if (user?.organizationId && assetTypeId) {
      await fetchRatingData(user.organizationId, assetTypeId);
    }
  };

  // CSV Export handler for Bridge and Slope inspections
  const handleExportBridgeSlopeInspections = async () => {
    setIsExportingAssets(true);
    setExportError(null);

    try {
      // Fetch inspections for Bridge and Slope asset types
      const api = require('../../services/api').default;
      const response = await api.get('/inspections/export-by-asset-type');

      const inspections = response.data || [];

      if (inspections.length === 0) {
        setExportError('No Bridge or Slope inspections found to export.');
        return;
      }

      // Generate CSV
      const csvContent = [
        'inspection_id,asset_type',
        ...inspections.map(insp => `${insp.inspection_id},${insp.asset_type}`)
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `bridge_slope_inspections_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export inspections:', error);
      setExportError('Failed to export inspections. Please try again.');
    } finally {
      setIsExportingAssets(false);
    }
  };

  // Inspection Type Management handlers
  const handleCreateInspectionType = (assetTypeId) => {
    setInspectionTypeDialog({
      open: true,
      mode: 'create',
      assetTypeId,
      inspectionType: null
    });
  };

  const handleEditInspectionType = (inspectionType) => {
    setInspectionTypeDialog({
      open: true,
      mode: 'edit',
      assetTypeId: inspectionType.asset_type_id,
      inspectionType
    });
  };

  const handleDeleteInspectionType = async (inspectionType) => {
    if (window.confirm(`Are you sure you want to delete "${inspectionType.name}"? This action cannot be undone.`)) {
      try {
        await inspectionTypesApi.deleteType(inspectionType.id);
        await refreshAssetManagementData(); // Refresh the data
        // You can add a success notification here if you have one
        console.log('Inspection type deleted successfully');
      } catch (error) {
        console.error('Error deleting inspection type:', error);
        // You can add an error notification here if you have one
        alert('Failed to delete inspection type. It may be in use by existing inspections.');
      }
    }
  };

  const renderDefectTypesTable = (defectTypesList) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Defect Type</strong></TableCell>
            <TableCell><strong>Code</strong></TableCell>
            <TableCell align="center">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <ScheduleIcon fontSize="small" />
                <strong>Temp Fix SLA</strong>
              </Box>
            </TableCell>
            <TableCell align="center">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <BuildIcon fontSize="small" />
                <strong>Permanent Fix SLA</strong>
              </Box>
            </TableCell>
            <TableCell align="center"><strong>SLA Status</strong></TableCell>
            <TableCell align="center"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {defectTypesList.map((defectType) => {
            const slaStatus = getSLAStatus(defectType.temp_fix_sla_hours, defectType.permanent_fix_sla_hours);
            
            return (
              <TableRow key={defectType.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {defectType.name}
                  </Typography>
                  {defectType.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {defectType.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={defectType.code || 'N/A'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="medium">
                    {formatSLAHours(defectType.temp_fix_sla_hours)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="medium">
                    {formatSLAHours(defectType.permanent_fix_sla_hours)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={slaStatus}
                    size="small"
                    color={getSLAStatusColor(slaStatus)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit SLA Settings">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditSLA(defectType)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Asset Type Management render functions
  const renderAssetTypeManagement = (assetTypeId) => {
    const assetTypeName = getAssetTypeName(assetTypeId);
    const assetDefectTypes = defectTypes.filter(dt => dt.asset_type_id === parseInt(assetTypeId));
    const assetSeveritySchemes = severitySchemes.filter(ss => ss.asset_type_id === parseInt(assetTypeId));
    const assetInspectionTypes = inspectionTypes.filter(it => it.asset_type_id === parseInt(assetTypeId));
    const assetRatingSchemes = Array.isArray(ratingSchemes[assetTypeId]) ? ratingSchemes[assetTypeId] : [];

    return (
      <Box>
        {/* Grid Layout for all 5 cards - using CSS Grid like AssetsPage */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(2, 1fr)', 
              lg: 'repeat(2, 1fr)'
            },
            gap: 3,
          }}
        >
          {/* Defect Types Card */}
          <Card sx={{ 
            minHeight: '350px',
            boxShadow: 2,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <BugReportIcon color="error" />
                <Typography variant="h6" fontWeight={600}>
                  Defect Types
                </Typography>
                <Chip 
                  label={`${assetDefectTypes.length} types`} 
                  size="small" 
                  color="error"
                  variant="outlined"
                />
              </Stack>
              
              {assetDefectTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No defect types configured for this asset type.
                </Typography>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  maxHeight: '240px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}>
                  {assetDefectTypes.map((defectType) => (
                    <Box 
                      key={defectType.id} 
                      sx={{ 
                        p: 1, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {defectType.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {defectType.code}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditDefectType(defectType)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
              <Button 
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleCreateDefectType(assetTypeId)}
              >
                Add Defect Type
              </Button>
            </CardActions>
          </Card>

          {/* Severity Schemes Card */}
          <Card sx={{ 
            minHeight: '350px',
            boxShadow: 2,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SpeedIcon color="warning" />
                <Typography variant="h6" fontWeight={600}>
                  Severity Schemes
                </Typography>
                <Chip 
                  label={`${assetSeveritySchemes.length} schemes`} 
                  size="small" 
                  color="warning"
                  variant="outlined"
                />
              </Stack>
              
              {assetSeveritySchemes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No severity schemes configured for this asset type.
                </Typography>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  maxHeight: '240px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}>
                  {assetSeveritySchemes.map((scheme) => {
                    const levels = severityLevels[scheme.id] || [];
                    return (
                      <Box 
                        key={scheme.id} 
                        sx={{ 
                          p: 1, 
                          border: '1px solid', 
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {scheme.name}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditSeverityScheme(scheme)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {levels.map((level) => (
                            <Chip 
                              key={level.id}
                              label={`${level.name} (${level.value})`}
                              size="small"
                              sx={{ bgcolor: level.color_code, color: 'white', fontSize: '0.7rem' }}
                              onClick={() => handleEditSeverityLevel(level, scheme.id)}
                            />
                          ))}
                          <Chip 
                            label="+ Add Level"
                            size="small"
                            variant="outlined"
                            onClick={() => handleCreateSeverityLevel(scheme.id)}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
              <Button 
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleCreateSeverityScheme(assetTypeId)}
              >
                Add Severity Scheme
              </Button>
            </CardActions>
          </Card>

          {/* Inspection Types Card */}
          <Card sx={{ 
            minHeight: '350px',
            boxShadow: 2,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CheckBoxIcon color="info" />
                <Typography variant="h6" fontWeight={600}>
                  Inspection Types
                </Typography>
                <Chip 
                  label={`${assetInspectionTypes.length} types`} 
                  size="small" 
                  color="info"
                  variant="outlined"
                />
              </Stack>
              
              {assetInspectionTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No inspection types configured for this asset type.
                </Typography>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  maxHeight: '240px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}>
                  {assetInspectionTypes.map((inspectionType) => (
                    <Box 
                      key={inspectionType.id} 
                      sx={{ 
                        p: 1, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {inspectionType.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {inspectionType.cycle_frequency ? `Cycle: ${inspectionType.cycle_frequency}` : 'No cycle set'}
                          {inspectionType.is_mandatory && ' • Mandatory'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditInspectionType(inspectionType)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteInspectionType(inspectionType)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
              <Button 
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleCreateInspectionType(assetTypeId)}
              >
                Add Inspection Type
              </Button>
            </CardActions>
          </Card>

          {/* Rating Schemes Card */}
          <Card sx={{ 
            minHeight: '350px',
            boxShadow: 2,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <GradeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Rating Schemes
                </Typography>
                <Chip
                  label={`${assetRatingSchemes.length} schemes`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
              
              {assetRatingSchemes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No rating schemes configured for this asset type.
                </Typography>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  maxHeight: '240px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}>
                  {assetRatingSchemes.map((scheme) => {
                    const levels = ratingLevels[scheme.id] || [];
                    return (
                      <Box
                        key={scheme.id}
                        sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {scheme.name}
                          </Typography>
                          <IconButton size="small" onClick={() => handleEditRatingScheme(scheme)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {levels.map((level) => (
                            <Chip
                              key={level.id}
                              label={`${level.name} (${level.value})`}
                              size="small"
                              sx={{ bgcolor: level.color_code, color: 'white', fontSize: '0.7rem' }}
                              onClick={() => handleEditRatingLevel(level, scheme.id)}
                            />
                          ))}
                          <Chip
                            label="+ Add Level"
                            size="small"
                            variant="outlined"
                            onClick={() => handleCreateRatingLevel(scheme.id)}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleCreateRatingScheme(assetTypeId)}
              >
                Add Rating Scheme
              </Button>
            </CardActions>
          </Card>

          {/* Dynamic Inspection Cycle Rules Card */}
          <Card sx={{ 
            minHeight: '350px',
            boxShadow: 2,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <InspectionCycleRules assetTypeId={assetTypeId} organizationId={user.organizationId} />
          </Card>
        </Box>
      </Box>
    );
  };



  const { grouped, generalDefects } = groupDefectTypesByAsset();

  // Show loading while authentication is being determined
  if (authLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Authenticating...
          </Typography>
        </Stack>
      </Container>
    );
  }
  
  // Show error if user is not available
  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          Authentication required. Please log in to access settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure system preferences and SLA policies
          </Typography>
        </Box>
      </Stack>

      {/* User Profile Overview */}
      <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            User Profile
          </Typography>
        </Stack>

        {loading && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading user information...
            </Typography>
          </Stack>
        )}
        
        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}
        
        {userData && !loading && (
          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Current User:</Typography>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="body2">
                <strong>{userData.fullName}</strong> ({userData.username})
              </Typography>
              <Chip 
                label={userData.organizationName || 'No Organization'} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              <Chip 
                label={userData.role} 
                size="small" 
                color="secondary" 
                variant="outlined"
              />
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Data Export Section */}
      <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <DownloadIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Data Export
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Export asset data for reporting and analysis purposes
        </Typography>

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}

        <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Bridge & Slope Inspections Export
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Export all Bridge and Slope inspections to CSV (inspection_id, asset_type)
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={isExportingAssets ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportBridgeSlopeInspections}
              disabled={isExportingAssets}
            >
              {isExportingAssets ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Work Order Auto-Assignment Section - Only for Managers */}
      {userData && userData.role === 'manager' && (
        <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Work Order Auto-Assignment
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure automatic work order creation when defects are validated
          </Typography>

          {autoAssignError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAutoAssignError(null)}>
              {autoAssignError}
            </Alert>
          )}

          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Auto-assign validated defects
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, validated defects will automatically create a work order and assign to network/section users for that location
                </Typography>
              </Box>
              {autoAssignLoading ? (
                <CircularProgress size={24} />
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoAssignEnabled}
                      onChange={handleAutoAssignToggle}
                      disabled={autoAssignSaving}
                      color="primary"
                    />
                  }
                  label={autoAssignSaving ? 'Saving...' : (autoAssignEnabled ? 'Enabled' : 'Disabled')}
                  labelPlacement="start"
                />
              )}
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Asset Type Management Section - Only for Managers */}
      {userData && userData.role === 'manager' && (
        <Paper sx={{ p: 3, borderRadius: 1, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Asset Type Management
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage defect types and severity schemes for each asset type in your organization
          </Typography>
          
          {/* Loading State */}
          {assetManagementLoading && (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading asset management data...
              </Typography>
            </Stack>
          )}
          
          {/* Error State */}
          {assetManagementError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assetManagementError}
            </Alert>
          )}
          
          {/* Content - Show asset types with their defect types and severity schemes */}
          {!assetManagementLoading && assetTypes.length > 0 && (
            <>
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ mb: 3 }}
              >
                <Typography variant="h6">
                  Asset Types ({assetTypes.length} total)
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={refreshAssetManagementData}
                >
                  Refresh
                </Button>
              </Stack>
              
              {/* Asset Types Accordions */}
              {assetTypes.map((assetType) => (
                <Accordion 
                  key={assetType.id}
                  expanded={expandedPanels[`asset-${assetType.id}`] || false}
                  onChange={handleAccordionChange(`asset-${assetType.id}`, assetType.id)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CategoryIcon color="primary" />
                      <Typography variant="h6">
                        {assetType.name}
                      </Typography>
                      <Chip 
                        label={assetType.category_name} 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${defectTypes.filter(dt => dt.asset_type_id === assetType.id).length} defect types`} 
                        size="small" 
                        color="error"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${severitySchemes.filter(ss => ss.asset_type_id === assetType.id).length} severity schemes`} 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${inspectionTypes.filter(it => it.asset_type_id === assetType.id).length} inspection types`} 
                        size="small" 
                        color="info"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${Array.isArray(ratingSchemes[assetType.id]) ? ratingSchemes[assetType.id].length : 0} rating schemes`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Configure defect types, severity schemes, inspection types, rating schemes, and cycle rules for {assetType.name.toLowerCase()} assets.
                    </Typography>
                    {renderAssetTypeManagement(assetType.id)}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Paper>
      )}
      
      {/* SLA Management Section */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <TimerIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            SLA Management
          </Typography>
        </Stack>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure Service Level Agreements for defect types and response times
        </Typography>
        
        {/* Loading State */}
        {defectTypesLoading && (
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading defect types...
            </Typography>
          </Stack>
        )}
        
        {/* Error State */}
        {defectTypesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {defectTypesError}
          </Alert>
        )}
        
        {/* Empty State */}
        {!defectTypesLoading && !defectTypesError && defectTypes.length === 0 && userData && (
          <Alert severity="info">
            No defect types found for organization "{userData.organizationName}". 
            Create defect types first to configure SLA settings.
          </Alert>
        )}
        
        {/* Content */}
        {!defectTypesLoading && defectTypes.length > 0 && (
          <>
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center" 
              sx={{ mb: 3 }}
            >
              <Typography variant="h6">
                Defect Types by Asset Category ({defectTypes.length} total)
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => userData?.organizationId && fetchDefectTypes(userData.organizationId)}
              >
                Refresh
              </Button>
            </Stack>
            
            {/* General Defect Types */}
            {generalDefects.length > 0 && (
              <Accordion 
                expanded={expandedPanels['general'] || false}
                onChange={handleAccordionChange('general')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CategoryIcon color="action" />
                    <Typography variant="h6">
                      General Defect Types
                    </Typography>
                    <Chip 
                      label={`${generalDefects.length} types`} 
                      size="small" 
                      color="default"
                      variant="outlined"
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Defect types that apply to multiple asset categories or are not specific to any particular asset type.
                  </Typography>
                  {renderDefectTypesTable(generalDefects)}
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Asset Type Specific Defect Types */}
            {Object.keys(grouped).map((assetTypeId) => {
              const defectTypesForAsset = grouped[assetTypeId];
              const assetTypeName = getAssetTypeName(parseInt(assetTypeId));
              
              return (
                <Accordion 
                  key={assetTypeId}
                  expanded={expandedPanels[assetTypeId] || false}
                  onChange={handleAccordionChange(assetTypeId)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CategoryIcon color="primary" />
                      <Typography variant="h6">
                        {assetTypeName}
                      </Typography>
                      <Chip 
                        label={`${defectTypesForAsset.length} types`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Defect types specific to {assetTypeName.toLowerCase()} assets.
                    </Typography>
                    {renderDefectTypesTable(defectTypesForAsset)}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            
            <Divider sx={{ my: 3 }} />
            
            {/* Legend */}
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>SLA Status Legend:</strong> 
                <Chip label="Both" size="small" color="success" variant="outlined" sx={{ mx: 0.5 }} />
                = Both temporary and permanent fix SLAs configured |
                <Chip label="Temp Only" size="small" color="warning" variant="outlined" sx={{ mx: 0.5 }} />
                = Only temporary fix SLA configured |
                <Chip label="Perm Only" size="small" color="info" variant="outlined" sx={{ mx: 0.5 }} />
                = Only permanent fix SLA configured |
                <Chip label="None" size="small" color="default" variant="outlined" sx={{ mx: 0.5 }} />
                = No SLA configured
              </Typography>
            </Box>
          </>
        )}
      </Paper>

      {/* Edit SLA Dialog */}
      <EditSLADialog
        open={editSLADialog.open}
        onClose={handleCloseSLADialog}
        defectType={editSLADialog.defectType}
        userData={userData}
        onUpdate={handleSLAUpdate}
      />

      {/* Asset Type Management Dialogs */}
      <DefectTypeDialog
        open={defectTypeDialog.open}
        onClose={() => setDefectTypeDialog({ open: false, mode: 'create', assetTypeId: null, defectType: null })}
        mode={defectTypeDialog.mode}
        assetTypeId={defectTypeDialog.assetTypeId}
        defectType={defectTypeDialog.defectType}
        userData={userData}
        onUpdate={refreshAssetManagementData}
      />

      <SeveritySchemeDialog
        open={severitySchemeDialog.open}
        onClose={() => setSeveritySchemeDialog({ open: false, mode: 'create', assetTypeId: null, scheme: null })}
        mode={severitySchemeDialog.mode}
        assetTypeId={severitySchemeDialog.assetTypeId}
        scheme={severitySchemeDialog.scheme}
        userData={userData}
        onUpdate={refreshAssetManagementData}
      />

      <SeverityLevelDialog
        open={severityLevelDialog.open}
        onClose={() => setSeverityLevelDialog({ open: false, mode: 'create', schemeId: null, level: null })}
        mode={severityLevelDialog.mode}
        schemeId={severityLevelDialog.schemeId}
        level={severityLevelDialog.level}
        onUpdate={refreshRatingsForAssetType}
      />

      <InspectionTypeDialog
        open={inspectionTypeDialog.open}
        onClose={() => setInspectionTypeDialog({ open: false, mode: 'create', assetTypeId: null, inspectionType: null })}
        mode={inspectionTypeDialog.mode}
        assetTypeId={inspectionTypeDialog.assetTypeId}
        inspectionType={inspectionTypeDialog.inspectionType}
        userData={userData}
        onUpdate={refreshAssetManagementData}
      />

      {/* Rating Management Dialogs */}
      <RatingSchemeDialog
        open={ratingSchemeDialog.open}
        onClose={() => setRatingSchemeDialog({ open: false, mode: 'create', assetTypeId: null, scheme: null })}
        mode={ratingSchemeDialog.mode}
        assetTypeId={ratingSchemeDialog.assetTypeId}
        scheme={ratingSchemeDialog.scheme}
        userData={userData}
        onUpdate={refreshRatingsForAssetType}
      />

      <RatingLevelDialog
        open={ratingLevelDialog.open}
        onClose={() => setRatingLevelDialog({ open: false, mode: 'create', schemeId: null, level: null })}
        mode={ratingLevelDialog.mode}
        schemeId={ratingLevelDialog.schemeId}
        level={ratingLevelDialog.level}
        onUpdate={refreshRatingsForAssetType}
        ratingSchemes={Object.values(ratingSchemes).flat()}
      />
    </Container>
  );
};

export default SettingsPage; 